import {
  clearUnfinishedRequestFiles,
  sweepUnfinishedRequestFiles,
  UNFINISHED_REQUEST_STORAGE_PREFIX,
} from './unfinishedRequestFiles'

let generation = 0

/**
 * Changes every time a logout wipes the user's storage, in this tab or in
 * another one. Something that saves later (a debounced write, an unmount
 * flush) compares it with the value it started with and stops, so it cannot
 * write the user's data back.
 */
export const getUserStorageGeneration = () => generation

// Another tab cleared localStorage, which only a logout does here (see below):
// this tab's pending saves must not put the user's data back either.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === null) {
      generation += 1
    }
  })
}

/**
 * Clears what the signed-in user left in browser storage: localStorage, and
 * the unfinished-request attachments in IndexedDB, which localStorage.clear()
 * does not reach.
 *
 * `keepUnfinishedRequestsOf` is for a session that ended on its own (the
 * token could not be refreshed), and names the user whose session it was.
 * The request they were submitting when it expired is exactly what ADN-597
 * keeps, so theirs stay for them to finish after signing back in. Any other
 * account's, on a shared device, are removed with their attachments. An
 * explicit logout removes everything.
 */
export const clearUserStorage = ({ keepUnfinishedRequestsOf }: { keepUnfinishedRequestsOf?: string | number } = {}) => {
  const keptPrefix = keepUnfinishedRequestsOf
    ? `${UNFINISHED_REQUEST_STORAGE_PREFIX}:${keepUnfinishedRequestsOf}:`
    : null
  try {
    if (keptPrefix) {
      // Key by key rather than clear(): other tabs read a full clear as a
      // logout and would stop keeping the form they have open.
      const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      keys
        .filter((key): key is string => !!key && !key.startsWith(keptPrefix))
        .forEach((key) => localStorage.removeItem(key))
    } else {
      localStorage.clear()
    }
  } catch {
    // Storage unavailable: nothing to clear.
  }
  if (keptPrefix) {
    // Attachments of every record just removed go with it; the kept user's stay.
    void sweepUnfinishedRequestFiles((key) => {
      if (!key.startsWith(keptPrefix)) return undefined
      try {
        return (JSON.parse(localStorage.getItem(key) || 'null') as { id?: string } | null)?.id
      } catch {
        return undefined
      }
    })
    return
  }
  generation += 1
  void clearUnfinishedRequestFiles()
}
